import { useEffect, useRef, useState } from 'react';
import type { DiscordAccount, DiscordConnectStartResponse, MeResponse } from '@hearth/shared';
import type { Api } from '../api';
import { Avatar } from './Avatar';
import { DiscordMark } from './SignIn';

// The top bar's account menu (M14.3, as the canvas board): who you are and how you signed in,
// Connect Discord (or the account connected, which Google sign-ins may disconnect), Sign out.

/** Where Connect Discord keeps its `state` for the tab, checked when Discord sends the browser back. */
export const DISCORD_STATE_KEY = 'hearth.discordState';

export function AccountMenu({ me, name, api, onSignOut }: { me: MeResponse; name: string; api?: Api; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  // What the menu changed since `me` was read (disconnecting); undefined = as read.
  const [discord, setDiscord] = useState<DiscordAccount | null>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => box.current?.contains(e.target as Node) || setOpen(false);
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const act = (work: () => Promise<void>) => {
    setBusy(true);
    setError('');
    work().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setBusy(false);
    });
  };

  return (
    <div className="account" ref={box}>
      <button className="account-button" type="button" aria-expanded={open} aria-controls="account-menu" onClick={() => setOpen(!open)}>
        <Avatar name={name} picture={me.picture} />
        <span className="who-name">{name}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        <span className="sr-only">Your account</span>
      </button>
      {open && (
        <AccountMenuView
          me={me}
          name={name}
          discord={discord === undefined ? me.discord : (discord ?? undefined)}
          busy={busy}
          error={error}
          onConnect={() =>
            act(async () => {
              const { url, state } = await api!.post<DiscordConnectStartResponse>('/v1/me/discord/start');
              sessionStorage.setItem(DISCORD_STATE_KEY, state);
              window.location.assign(url);
            })
          }
          onDisconnect={() =>
            act(async () => {
              await api!.delete('/v1/me/discord');
              setDiscord(null);
              setBusy(false);
            })
          }
          onSignOut={onSignOut}
        />
      )}
    </div>
  );
}

/** The open menu, given what's known (no requests: easy to test). */
export function AccountMenuView({ me, name, discord, busy, error, onConnect, onDisconnect, onSignOut }: {
  me: MeResponse;
  name: string;
  discord: DiscordAccount | undefined;
  busy: boolean;
  error: string;
  onConnect: () => void;
  onDisconnect: () => void;
  onSignOut: () => void;
}) {
  const discordSignIn = me.provider === 'Discord';
  return (
    <div className="account-menu" id="account-menu">
      <div className="account-who">
        <Avatar name={name} picture={me.picture} />
        <div>
          <strong>{name}</strong>
          {me.provider && <span className="quiet">{`Signed in with ${me.provider}`}</span>}
        </div>
      </div>
      <hr />
      {discord ? (
        <div className="account-discord">
          <span><DiscordMark />{`Discord: ${discord.username ?? 'connected'}`}</span>
          {!discordSignIn && <button className="link-button" type="button" disabled={busy} onClick={onDisconnect}>Disconnect</button>}
        </div>
      ) : (
        <button className="account-item" type="button" disabled={busy} onClick={onConnect}>
          <DiscordMark />
          {busy ? 'Opening Discord…' : 'Connect Discord'}
        </button>
      )}
      {error && <p className="notice" role="alert">{error}</p>}
      <hr />
      <button className="account-item" type="button" onClick={onSignOut}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>
        Sign out
      </button>
    </div>
  );
}
